/**
 * The form a waiting permission request shows as, where a question would
 * show: how a pending approval is written into a `FormInfo`'s metadata for
 * a session on the person's own computer, and how the transcript and the
 * permission card read it back. The writer and the reader share one module,
 * so the metadata's keys are named once, and it imports nothing of the local
 * runtime, so the shared transcript model does not either.
 */
import type { PendingApproval } from '@/lib/code-sessions/local-agent/consent';
import type { LocalSessionSnapshot } from '@/lib/code-sessions/local-agent/operations';
import type { FormInfo, PermissionRequest } from '@opencode/client/promise';

/**
 * `metadata.kind` of the form a waiting permission request shows as, which
 * the permission card answers.
 */
export const PERMISSION_FORM_KIND = 'permission';

/**
 * A pending approval as the form the question card answers, shown where a
 * question would show. The consent guard keys its answer on the whole command
 * and the working folder it serves, so the form carries those, not OpenCode's
 * split resources; the raw request the approval stands for names the tool
 * call that waits. The card reads it all back from `metadata`; the one field
 * OpenCode's form type requires names the action and what it asks for.
 */
export function permissionForm(approval: PendingApproval, request?: PermissionRequest): FormInfo {
  const shown = approval.command ?? approval.resources.join('\n');
  return {
    id: approval.requestId,
    sessionID: approval.sessionId,
    title: 'Waiting for your approval',
    metadata: {
      kind: PERMISSION_FORM_KIND,
      action: approval.action,
      command: approval.command,
      workingFolder: approval.workingFolder,
      sessionFolder: approval.sessionFolder,
      resources: [...approval.resources],
      ...(approval.message === undefined ? {} : { message: approval.message }),
      background: approval.background,
      ...(approval.timeoutMs === undefined ? {} : { timeoutMs: approval.timeoutMs }),
      ...(request?.source?.type === 'tool' && typeof request.source.id === 'string'
        ? { callId: request.source.id }
        : {}),
    },
    fields: [{ key: 'decision', type: 'string', title: approval.action, description: shown }],
  };
}

/**
 * The forms a snapshot's pending approvals make, each joined to the raw
 * request it stands for so the tool call that waits is named.
 */
export function approvalForms(snapshot: LocalSessionSnapshot): FormInfo[] {
  const requestById = new Map(snapshot.permissions.map((request) => [request.id, request]));
  return snapshot.approvals.map((approval) =>
    permissionForm(approval, requestById.get(approval.requestId)),
  );
}

/** What `permissionForm` put in a form's metadata, read back by the question card. */
export type PermissionFormDetails = {
  action: string;
  /**
   * The whole command; null for an action with no command text and for a
   * shell call the consent guard could not read back, which takes Deny
   * alone. Only a command the guard read can be allowed for the session.
   */
  command: string | null;
  /**
   * The folder a shell command runs in; empty for any other request, which runs nothing in a
   * folder, and for one that names none.
   */
  workingFolder: string;
  /**
   * The session's folder: named beside the working folder when they differ, and alone for a
   * request that runs nothing, so a location outside it says what it is outside of; empty when
   * unknown.
   */
  sessionFolder: string;
  /** OpenCode's split resources, shown when the command is null. */
  resources: string[];
  message?: string;
  /** The tool call that waits on the request, when the request names one. */
  callId?: string;
  /** Whether the command keeps running in the background after it starts. */
  background: boolean;
  /** The time limit the call sets for a background command, in milliseconds; none means no limit. */
  timeoutMs?: number;
};

/** The permission request a form stands for, or null for a question the agent asked. */
export function permissionFormDetails(
  form: Pick<FormInfo, 'metadata'>,
): PermissionFormDetails | null {
  const metadata = form.metadata;
  if (metadata?.kind !== PERMISSION_FORM_KIND || typeof metadata.action !== 'string') {
    return null;
  }
  const resources = Array.isArray(metadata.resources)
    ? metadata.resources.filter((resource): resource is string => typeof resource === 'string')
    : [];
  return {
    action: metadata.action,
    command: typeof metadata.command === 'string' ? metadata.command : null,
    // The consent guard gives every request the session folder as its working folder, but only a
    // shell command runs in one: a location request, say, would read "Runs in:" a folder for a
    // file it only reads or edits.
    workingFolder:
      metadata.action === 'shell' && typeof metadata.workingFolder === 'string'
        ? metadata.workingFolder
        : '',
    sessionFolder: typeof metadata.sessionFolder === 'string' ? metadata.sessionFolder : '',
    resources,
    ...(typeof metadata.message === 'string' ? { message: metadata.message } : {}),
    ...(typeof metadata.callId === 'string' ? { callId: metadata.callId } : {}),
    background: metadata.background === true,
    // `permissionForm` writes a limit only above 0, OpenCode's "no limit" (`exactCommandOf`).
    ...(typeof metadata.timeoutMs === 'number' ? { timeoutMs: metadata.timeoutMs } : {}),
  };
}
